import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@altimateai/lego";
import {
  submitBatchEvaluation,
  type BatchEvaluationRequest,
  type Criterion,
} from "../../services/evals";

interface UseRunEvaluationProps {
  localAgents: Array<{
    id: string;
    name: string;
    description: string;
    prompt: string;
    model: string;
  }>;
  backendAgentsMap: Record<string, { name: string; description: string; prompt: string }>;
  criteria: Criterion[] | undefined;
  onEvaluate?: (data: { agentIds: string[]; testCaseIds: string[]; criteriaIds: string[] }) => void;
}

export const useRunEvaluation = ({
  localAgents,
  backendAgentsMap,
  criteria,
  onEvaluate,
}: UseRunEvaluationProps) => {
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [selectedTestCases, setSelectedTestCases] = useState<string[]>([]);
  const [selectedCriteria, setSelectedCriteria] = useState<string[]>([]);
  const [selectedDatamates, setSelectedDatamates] = useState<string[]>([]);
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<number[]>([]);
  const [runName, setRunName] = useState<string>("");
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  const evaluationMutation = useMutation({
    mutationFn: submitBatchEvaluation,
    onSuccess: data => {
      toast({
        title: "Evaluation Submitted Successfully",
        description: data?.message,
      });
      if (data?.job_id) {
        setCurrentJobId(data.job_id);
      }
      setProgressDialogOpen(true);
      setSelectedAgents([]);
      setSelectedTestCases([]);
      setSelectedCriteria([]);
      setSelectedDatamates([]);
      setSelectedKnowledgeBases([]);
      setRunName("");
    },
    onError: (error: Error) => {
      toast({
        title: "Evaluation Failed",
        description: error.message || "Failed to submit evaluation",
        variant: "destructive",
      });
    },
  });

  const handleToggleAgent = (agentId: string) => {
    setSelectedAgents(prev =>
      prev.includes(agentId) ? prev.filter(id => id !== agentId) : [...prev, agentId]
    );
  };

  const handleToggleTestCase = (testCaseId: string) => {
    setSelectedTestCases(prev =>
      prev.includes(testCaseId) ? prev.filter(id => id !== testCaseId) : [...prev, testCaseId]
    );
  };

  const handleToggleCriterion = (criterionId: string) => {
    setSelectedCriteria(prev =>
      prev.includes(criterionId) ? prev.filter(id => id !== criterionId) : [...prev, criterionId]
    );
  };

  const handleSelectAllAgents = () => {
    const allAgentIds = [...localAgents.map(a => a.id), ...Object.keys(backendAgentsMap || {})];
    setSelectedAgents(allAgentIds);
  };

  const handleDeselectAllAgents = () => setSelectedAgents([]);

  const handleSelectAllTestCases = (testCases: Array<{ id: string }>) => {
    const allTestCaseIds = testCases.map(tc => tc.id);
    setSelectedTestCases(allTestCaseIds);
  };

  const handleDeselectAllTestCases = () => setSelectedTestCases([]);

  const handleSelectAllCriteria = () => {
    const allCriteriaIds = (Array.isArray(criteria) ? criteria : []).map(c => c.id);
    setSelectedCriteria(allCriteriaIds);
  };

  const handleDeselectAllCriteria = () => setSelectedCriteria([]);

  const handleEvaluate = () => {
    if (selectedAgents.length === 0) {
      toast({
        title: "No Agents Selected",
        description: "Please select at least one agent to evaluate",
        variant: "destructive",
      });
      return;
    }

    if (selectedTestCases.length === 0) {
      toast({
        title: "No Test Cases Selected",
        description: "Please select at least one test case to evaluate",
        variant: "destructive",
      });
      return;
    }

    if (selectedCriteria.length === 0) {
      toast({
        title: "No Criteria Selected",
        description: "Please select at least one criterion to evaluate",
        variant: "destructive",
      });
      return;
    }

    const allAgents = selectedAgents
      .map(agentId => {
        if (agentId.startsWith("local-")) {
          const agent = localAgents.find(a => a.id === agentId);
          if (!agent) return null;
          return {
            id: agent.id,
            name: agent.name,
            description: agent.description,
            prompt: agent.prompt,
            model: agent.model,
            is_local: true,
          };
        }
        const backendAgent = backendAgentsMap[agentId];
        if (!backendAgent) return null;
        return {
          id: agentId,
          name: backendAgent.name,
          description: backendAgent.description,
          prompt: backendAgent.prompt,
          model: "",
          is_local: false,
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      name: string;
      description: string;
      prompt: string;
      model: string;
      is_local: boolean;
    }>;

    const selectedCriterionObjects = (Array.isArray(criteria) ? criteria : []).filter(c =>
      selectedCriteria.includes(c.id)
    );

    const payload: BatchEvaluationRequest = {
      agents: allAgents,
      test_case_ids: selectedTestCases,
      criteria: selectedCriterionObjects,
      run_name: runName.trim() || undefined,
      global_context: {
        datamate_ids: selectedDatamates,
        knowledge_base_ids: selectedKnowledgeBases,
      },
    };

    console.log("Submitting evaluation:", payload);

    evaluationMutation.mutate(payload);

    if (onEvaluate) {
      onEvaluate({
        agentIds: selectedAgents,
        testCaseIds: selectedTestCases,
        criteriaIds: selectedCriteria,
      });
    }
  };

  return {
    selectedAgents,
    selectedTestCases,
    selectedCriteria,
    selectedDatamates,
    selectedKnowledgeBases,
    runName,
    setSelectedDatamates,
    setSelectedKnowledgeBases,
    setRunName,
    progressDialogOpen,
    currentJobId,
    setProgressDialogOpen,
    setCurrentJobId,
    evaluationMutation,
    handleToggleAgent,
    handleToggleTestCase,
    handleToggleCriterion,
    handleSelectAllAgents,
    handleDeselectAllAgents,
    handleSelectAllTestCases,
    handleDeselectAllTestCases,
    handleSelectAllCriteria,
    handleDeselectAllCriteria,
    handleEvaluate,
  };
};
